import { unreachable } from 'module/helpers/unreachable';
import {
  OldAnswerTypeOption,
  OldCustomSectionType,
  OldGroupByOption,
  OldGenerateAsOption,
  OldParticipantGroupOption,
  OldCustomSectionConfiguration,
  OldSectionComponentType,
  CustomSectionType,
  GroupByOption,
  GenerateAsOption,
  ParticipantGroupOption,
  CustomSectionContentState,
  AnswerTypeOption,
} from './types';

const oldToSectionType = (
  sectionType: OldCustomSectionType,
): CustomSectionType => {
  switch (sectionType) {
    case OldCustomSectionType.Question:
      return CustomSectionType.Question;
    case OldCustomSectionType.Action:
      return CustomSectionType.Action;
    case OldCustomSectionType.ExtractInfo:
      return CustomSectionType.ExtractInfo;
    case OldCustomSectionType.MeetingDetails:
      return CustomSectionType.MeetingDetails;
    case OldCustomSectionType.AccountDetails:
      return CustomSectionType.AccountDetails;
    case OldCustomSectionType.Signal:
      return CustomSectionType.SignalDetails;
    case OldCustomSectionType.NonExtract:
      return unreachable(sectionType as never);
    default:
      return unreachable(sectionType);
  }
};

const oldToGroupByOption = (groupBy: OldGroupByOption): GroupByOption => {
  switch (groupBy) {
    case OldGroupByOption.Topics:
      return GroupByOption.Topics;
    case OldGroupByOption.Company:
      return GroupByOption.Company;
    case OldGroupByOption.Participants:
      return GroupByOption.Participants;
    case OldGroupByOption.None:
      return GroupByOption.None;
    default:
      return unreachable(groupBy);
  }
};
const oldToAnswerTypeOption = (
  answerType: OldAnswerTypeOption,
): AnswerTypeOption => {
  switch (answerType) {
    case OldAnswerTypeOption.SingleChoice:
      return AnswerTypeOption.SingleChoice;
    case OldAnswerTypeOption.MultiChoice:
      return AnswerTypeOption.MultiChoice;
    default:
      return unreachable(answerType);
  }
};
const oldToGenerateAsOption = (
  generateAs: OldGenerateAsOption,
): GenerateAsOption => {
  switch (generateAs) {
    case OldGenerateAsOption.Paragraph:
      return GenerateAsOption.Paragraph;
    case OldGenerateAsOption.Bullet:
      return GenerateAsOption.Bullet;
    case OldGenerateAsOption.Number:
      return GenerateAsOption.Number;
    case OldGenerateAsOption.Checklist:
      return GenerateAsOption.Checklist;
    default:
      return unreachable(generateAs);
  }
};
const oldToParticipantGroupOption = (
  participantGroup: OldParticipantGroupOption,
): ParticipantGroupOption => {
  switch (participantGroup) {
    case OldParticipantGroupOption.All:
      return ParticipantGroupOption.All;
    case OldParticipantGroupOption.CustomerParticipants:
      return ParticipantGroupOption.CustomerParticipants;
    case OldParticipantGroupOption.MyTeam:
      return ParticipantGroupOption.MyTeam;
    case OldParticipantGroupOption.Custom:
      return ParticipantGroupOption.Custom;
    default:
      return unreachable(participantGroup);
  }
};
const oldToNodeConfig = (nodeConfig: OldCustomSectionConfiguration): any => {
  const { groupBy, generateAs, participants, choices } = nodeConfig;

  return {
    ...nodeConfig,
    groupBy: groupBy != null ? oldToGroupByOption(groupBy) : undefined,
    generateAs:
      generateAs != null ? oldToGenerateAsOption(generateAs) : undefined,
    participants:
      participants != null
        ? oldToParticipantGroupOption(participants)
        : undefined,
    choices:
      choices != null
        ? {
            ...choices,
            answerType:
              choices.answerType != null
                ? oldToAnswerTypeOption(choices.answerType)
                : choices.answerType,
          }
        : undefined,
  };
};
const oldToContentState = (
  contentState: OldSectionComponentType,
): CustomSectionContentState => {
  switch (contentState) {
    case OldSectionComponentType.Prompt:
      return CustomSectionContentState.Prompt;
    case OldSectionComponentType.Generated:
      return CustomSectionContentState.Generated;
    case 'GenerateSectionComponent' as any:
      return CustomSectionContentState.Generated;
    default:
      return unreachable(contentState);
  }
};

// TODO: This is a temporary function to transform the old attributes to the new attributes
// TODO: Remove this function after the migration is done
export const transformOldAttributes = (attrs: any) => {
  const {
    sectionType,
    uuid,
    templateSectionId,
    payload,
    componentType,
    contentState,
    config,
    ...rest
  } = attrs;

  return {
    ...rest,
    contentState:
      contentState ??
      (componentType != null
        ? oldToContentState(componentType)
        : componentType),
    templateSectionId: templateSectionId ?? uuid,
    sectionType: oldToSectionType(sectionType) ?? sectionType,
    config: config ?? oldToNodeConfig(payload),
  };
};
const transformResponse = (response: any) => {
  if (response == null) {
    return response;
  }
  const isOldFormat =
    response.customFields != null || response.nativeFields != null;

  if (isOldFormat) {
    return {
      ...response.nativeFields,
      ...(response.nativeFields?.accountOwner
        ? {
            accountOwner: {
              firstName: response.nativeFields.accountOwner,
              fullName: response.nativeFields.accountOwner,
            },
          }
        : {}),
      fields: Object.values(response.customFields),
    };
  }

  return response;
};

export const transformJSONFromOld = (json: any) => {
  if (typeof json === 'string') {
    return json;
  }

  if (Array.isArray(json)) {
    return json.map(transformJSONFromOld);
  }

  if (['aiFillSection', 'blockDetailsSection'].includes(json.type)) {
    return {
      ...json,
      type: 'RL_CUSTOM_SECTION',
      attrs: transformOldAttributes(json.attrs),
    };
  }
  if (json.type === 'RL_CUSTOM_SECTION') {
    return {
      ...json,
      attrs: {
        ...json.attrs,
        response: json.attrs.response
          ? transformResponse(json.attrs.response)
          : {},
      },
    };
  }
  if (json.type === 'doc') {
    return {
      ...json,
      content: json.content.map(transformJSONFromOld),
    };
  }

  return json;
};
